import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { CheckCircle, CircleNotch, Clock, MapPin, XCircle } from '@phosphor-icons/react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  getPublicCheckinStatus,
  renovarPublicCheckin,
} from '@/api/lead-checkin/lead-checkin.actions'
import type {
  PublicCheckinRenovacaoData,
  PublicCheckinStatusData,
} from '@/api/lead-checkin/lead-checkin.actions'
import { getApiErrorMessage } from '@/lib/api-error'

export const Route = createFileRoute('/checkin/$token')({
  component: CheckinRenovacaoPage,
})

function CheckinRenovacaoPage() {
  const { token } = Route.useParams()
  const [isLoading, setIsLoading] = useState(true)
  const [isRenewing, setIsRenewing] = useState(false)
  const [statusData, setStatusData] = useState<PublicCheckinStatusData | null>(null)
  const [renovacaoResult, setRenovacaoResult] = useState<PublicCheckinRenovacaoData | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    loadStatus(() => cancelled)
    return () => {
      cancelled = true
    }
  }, [token])

  async function loadStatus(isCancelled?: () => boolean) {
    setIsLoading(true)
    setError(null)
    try {
      const response = await getPublicCheckinStatus(token)
      if (isCancelled?.()) return
      setStatusData(response.data)
    } catch (err: unknown) {
      if (isCancelled?.()) return
      setError(getApiErrorMessage(err, 'Erro ao verificar token'))
    } finally {
      if (!isCancelled?.()) setIsLoading(false)
    }
  }

  async function handleRenovar() {
    setIsRenewing(true)
    setError(null)
    try {
      const response = await renovarPublicCheckin(token)
      setRenovacaoResult(response.data)
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao renovar check-in'))
    } finally {
      setIsRenewing(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="flex items-center justify-center py-12">
            <CircleNotch className="h-8 w-8 animate-spin text-primary mr-3" />
            <span className="text-lg">Verificando...</span>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error && !statusData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="py-12 text-center">
            <XCircle className="h-16 w-16 mx-auto mb-4 text-red-400" />
            <h2 className="text-xl font-semibold mb-2">Link inválido</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (renovacaoResult) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-emerald-50">
        <Card className="w-full max-w-md mx-4 border-green-200">
          <CardContent className="py-12 text-center">
            <CheckCircle className="h-20 w-20 mx-auto mb-4 text-green-500" />
            <h2 className="text-2xl font-bold text-green-700 mb-2">
              Check-in renovado!
            </h2>
            <p className="text-green-600 mb-4">
              Você continua na fila para receber leads.
            </p>
            {renovacaoResult.proximaRenovacao && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground bg-white rounded-lg p-3 border">
                <Clock className="h-4 w-4" />
                Próxima renovação:{' '}
                {new Date(renovacaoResult.proximaRenovacao).toLocaleTimeString(
                  'pt-BR',
                  { hour: '2-digit', minute: '2-digit' },
                )}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-6">
              Pode fechar esta página.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-3 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <MapPin className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-xl">Renovação de Plantão</CardTitle>
          <CardDescription>
            {statusData?.dashboard}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center">
            <p className="text-lg font-medium">{statusData?.nome}</p>
            {statusData?.checkinAtivo ? (
              <div className="space-y-3 mt-4">
                {statusData.tempoRestanteMinutos !== null && (
                  <div className="flex items-center justify-center gap-2 text-sm bg-amber-50 text-amber-700 rounded-lg p-3 border border-amber-200">
                    <Clock className="h-4 w-4" />
                    {statusData.tempoRestanteMinutos > 0
                      ? `Seu check-in expira em ${statusData.tempoRestanteMinutos} minutos`
                      : 'Seu check-in está prestes a expirar!'}
                  </div>
                )}

                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button
                  size="lg"
                  className="w-full h-16 text-lg bg-green-600 hover:bg-green-700"
                  onClick={handleRenovar}
                  disabled={isRenewing}
                >
                  {isRenewing ? (
                    <CircleNotch className="mr-2 h-5 w-5 animate-spin" />
                  ) : (
                    <CheckCircle className="mr-2 h-5 w-5" />
                  )}
                  Estou aqui!
                </Button>

                <p className="text-xs text-muted-foreground">
                  Clique para confirmar sua presença e renovar o check-in.
                </p>
              </div>
            ) : statusData?.checkinPausado ? (
              <div className="mt-4 text-center">
                <p className="text-amber-600">Seu plantão está pausado.</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Acesse o sistema para retomar o plantão.
                </p>
              </div>
            ) : (
              <div className="mt-4 text-center">
                <XCircle className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
                <p className="text-muted-foreground">
                  Você não está em plantão.
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Acesse o sistema para fazer check-in.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
